import { Directive, ElementRef, Input, OnInit, Renderer2, OnDestroy, Output, EventEmitter } from '@angular/core';

@Directive({
    selector: '[appResizable]'
})
export class ResizableDirective implements OnInit, OnDestroy {
    @Input('appResizable') columnName: string;
    @Input() tableId: string;

    /**
     * When set to 'external', the directive will NOT read/write localStorage itself.
     * The parent component is responsible for persisting and restoring widths.
     * The directive will still emit widthResized events normally.
     */
    @Input() storageStrategy: 'default' | 'external' = 'default';

    /** Optionally inject the initial width (px) when using external storage strategy */
    @Input() initialWidth: number | null = null;

    @Output() widthResized = new EventEmitter<number>();
    @Output() resizeEnd = new EventEmitter<number>();

    private handle: HTMLElement;
    private isResizing = false;
    private wasDragged = false;
    private startX: number;
    private startWidth: number;
    private mouseMoveListener: () => void;
    private mouseUpListener: () => void;

    constructor(private el: ElementRef, private renderer: Renderer2) { }

    ngOnInit() {
        this.createHandle();

        if (this.storageStrategy === 'external') {
            // Apply initial width provided by parent (from structured JSON config)
            if (this.initialWidth && this.initialWidth > 0) {
                this.applyWidth(this.initialWidth);
                setTimeout(() => this.widthResized.emit(this.initialWidth));
            } else {
                setTimeout(() => this.widthResized.emit(this.el.nativeElement.offsetWidth));
            }
        } else {
            // Default: read/write from individual localStorage keys
            this.restoreWidth();
            setTimeout(() => {
                this.widthResized.emit(this.el.nativeElement.offsetWidth);
            });
        }
    }

    private applyWidth(width: number) {
        this.renderer.setStyle(this.el.nativeElement, 'width', `${width}px`);
        this.renderer.setStyle(this.el.nativeElement, 'min-width', `${width}px`);
        this.renderer.setStyle(this.el.nativeElement, 'max-width', `${width}px`);
    }

    private createHandle() {
        // Column must have position: relative for the handle to be positioned correctly
        this.renderer.setStyle(this.el.nativeElement, 'position', 'relative');
        this.renderer.setStyle(this.el.nativeElement, 'overflow', 'visible');

        this.handle = this.renderer.createElement('span');
        this.renderer.addClass(this.handle, 'resize-handle');
        this.renderer.appendChild(this.el.nativeElement, this.handle);

        this.renderer.listen(this.handle, 'mousedown', (event: MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();
            this.isResizing = true;
            this.wasDragged = false;
            this.startX = event.pageX;
            this.startWidth = this.el.nativeElement.offsetWidth;

            this.mouseMoveListener = this.renderer.listen('document', 'mousemove', (e: MouseEvent) => {
                if (this.isResizing) {
                    this.wasDragged = true;
                    const width = this.startWidth + (e.pageX - this.startX);
                    this.applyWidth(width);
                    this.widthResized.emit(width);
                }
            });

            this.mouseUpListener = this.renderer.listen('document', 'mouseup', () => {
                if (this.isResizing) {
                    this.isResizing = false;

                    // Emit resizeEnd event with the final width
                    this.resizeEnd.emit(this.el.nativeElement.offsetWidth);

                    // If the user actually dragged to resize, swallow the next click
                    // so the mat-sort-header is NOT triggered.
                    if (this.wasDragged) {
                        const suppressClick = (clickEvent: MouseEvent) => {
                            clickEvent.stopPropagation();
                            clickEvent.preventDefault();
                            this.el.nativeElement.removeEventListener('click', suppressClick, true);
                        };
                        // Use capture phase so we intercept before Angular/Material handlers
                        this.el.nativeElement.addEventListener('click', suppressClick, true);
                        // Safety cleanup in case click never fires
                        setTimeout(() => {
                            this.el.nativeElement.removeEventListener('click', suppressClick, true);
                        }, 300);
                    }

                    if (this.storageStrategy !== 'external') {
                        this.saveWidth(this.el.nativeElement.offsetWidth);
                    }
                    if (this.mouseMoveListener) this.mouseMoveListener();
                    if (this.mouseUpListener) this.mouseUpListener();
                }
            });
        });
    }

    private saveWidth(width: number) {
        if (this.tableId && this.columnName) {
            const key = `resizable-table-${this.tableId}-${this.columnName}`;
            localStorage.setItem(key, `${width}`);
        }
    }

    private restoreWidth() {
        if (this.tableId && this.columnName) {
            const key = `resizable-table-${this.tableId}-${this.columnName}`;
            const savedWidth = localStorage.getItem(key);
            if (savedWidth) {
                this.applyWidth(parseInt(savedWidth, 10));
                this.widthResized.emit(parseInt(savedWidth, 10));
            }
        }
    }

    ngOnDestroy() {
        if (this.mouseMoveListener) this.mouseMoveListener();
        if (this.mouseUpListener) this.mouseUpListener();
    }
}
